import React, { createContext, useContext, useState, ReactNode } from "react";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import type { Language, LanguageContextValue } from "../types/lang.types";

import de from "../locales/de.json";
import en from "../locales/en.json";
import ar from "../locales/ar.json";

i18n.use(initReactI18next).init({
  resources: {
    de: { translation: de },
    en: { translation: en },
    ar: { translation: ar },
  },
  lng: "de",
  fallbackLng: "de",
  interpolation: { escapeValue: false },
});

const LanguageContext = createContext<LanguageContextValue | undefined>(
  undefined,
);

export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>("de");

  const changeLanguage = (lang: Language): void => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  };

  const value: LanguageContextValue = {
    currentLanguage,
    changeLanguage,
    t: i18n.t.bind(i18n) as (key: string, options?: Record<string, unknown>) => string,
    languages: ["de", "en", "ar"],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
